
import React, { useState } from 'react';
import {
    Users, UserCheck, Briefcase, Network,
    Trash2, ShieldAlert, ArrowRight, UserPlus, DoorOpen,
    Check, Plus, Keyboard
} from 'lucide-react';
import { WorkflowNodeData } from '../../../types';
import { AsyncSelect } from '../../molecules/AsyncSelect';
import { useEditorConfig } from '../../../contexts/EditorContext';

interface AssignmentsTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

type AssignmentType = 'person' | 'role' | 'group' | 'leader' | 'schedulegroup';

interface AssignmentConfig {
    type: AssignmentType;
    blacklist: boolean;
    param?: string; // personId, roleId, groupId
    paramName?: string; // Display name for param
    arg?: string; // roleId (for group@role)
    argName?: string; // Display name for arg
    mode?: 'exact' | 'up' | 'down' | 'recursive' | 'direct'; // For Group/Leader variants
}

export const AssignmentsTab: React.FC<AssignmentsTabProps> = ({ data, onChange }) => {
    // We store assignments as an array of strings in data.assignments
    const assignments: string[] = data.assignments || [];
    const { services } = useEditorConfig();

    const [isAdding, setIsAdding] = useState(false);
    const [isManualEntry, setIsManualEntry] = useState(false);
    const [manualValue, setManualValue] = useState('');
    const [selectedType, setSelectedType] = useState<AssignmentType | null>(null);
    const [config, setConfig] = useState<AssignmentConfig>({
        type: 'person',
        blacklist: false,
    });

    const parseAssignment = (str: string) => {
        const isBlacklist = str.startsWith('!');
        const cleanStr = isBlacklist ? str.substring(1) : str;
        
        if (cleanStr === 'schedulegroup') return { type: 'schedulegroup', blacklist: isBlacklist, label: 'Schedule Groups' };
        
        // Check modifiers
        if (cleanStr.startsWith('groupsUpwards:')) {
            return { type: 'group', mode: 'up', param: cleanStr.split(':')[1], blacklist: isBlacklist };
        }
        if (cleanStr.startsWith('groupsDownwards:')) {
            return { type: 'group', mode: 'down', param: cleanStr.split(':')[1], blacklist: isBlacklist };
        }
        if (cleanStr.startsWith('leaders:')) {
            return { type: 'leader', mode: 'recursive', param: cleanStr.split(':')[1], blacklist: isBlacklist };
        }
        if (cleanStr.startsWith('leader:')) {
            return { type: 'leader', mode: 'direct', param: cleanStr.split(':')[1], blacklist: isBlacklist };
        }

        // Standard parsing
        const typeMatch = cleanStr.match(/^([a-z]+):/);
        if (!typeMatch) return { type: 'unknown', label: str, blacklist: isBlacklist };
        
        const type = typeMatch[1];
        const rest = cleanStr.substring(type.length + 1);
        const [param, arg] = rest.split('@');
        
        return {
            type,
            param,
            arg,
            blacklist: isBlacklist,
            mode: 'exact',
            original: str
        };
    };

    const formatAssignment = (cfg: AssignmentConfig): string => {
        const prefix = cfg.blacklist ? '!' : '';
        
        switch (cfg.type) {
            case 'schedulegroup': return `${prefix}schedulegroup`;
            case 'group':
                if (cfg.mode === 'up') return `${prefix}groupsUpwards:${cfg.param}`;
                if (cfg.mode === 'down') return `${prefix}groupsDownwards:${cfg.param}`;
                if (cfg.arg) return `${prefix}group:${cfg.param}@${cfg.arg}`;
                return `${prefix}group:${cfg.param}`;
            case 'leader':
                if (cfg.mode === 'recursive') return `${prefix}leaders:${cfg.param}`;
                return `${prefix}leader:${cfg.param}`;
            case 'person': return `${prefix}person:${cfg.param}`;
            case 'role': return `${prefix}role:${cfg.param}`;
            default: return '';
        }
    };

    const handleTypeSelect = (type: AssignmentType) => {
        setSelectedType(type);
        setConfig({
            type,
            blacklist: false,
            mode: (type === 'group' ? 'exact' : (type === 'leader' ? 'direct' : undefined))
        });
    };

    const handleAdd = () => {
        // Validate
        if (config.type !== 'schedulegroup' && !config.param) return;

        const newStr = formatAssignment(config);
        const newAssignments = [...assignments, newStr];
        onChange('assignments', newAssignments);
        
        // Reset
        setIsAdding(false);
        setSelectedType(null);
        setConfig({ type: 'person', blacklist: false });
    };

    const handleManualAdd = () => {
        const trimmed = manualValue.trim();
        if (!trimmed) return;
        // Basic format validation: must match known patterns
        const validPattern = /^!?(person|role|group|leader|leaders|groupsUpwards|groupsDownwards|schedulegroup)(:.+)?$/;
        if (!validPattern.test(trimmed)) return;
        const newAssignments = [...assignments, trimmed];
        onChange('assignments', newAssignments);
        setManualValue('');
    };

    const handleRemove = (index: number) => {
        const newAssignments = assignments.filter((_, i) => i !== index);
        onChange('assignments', newAssignments);
    };

    const getTypeIcon = (type: string, mode?: string) => {
        if (type === 'person') return <UserCheck size={16} />;
        if (type === 'role') return <Briefcase size={16} />;
        if (type === 'group') {
            if (mode === 'up') return <ArrowRight size={16} className="-rotate-90" />;
            if (mode === 'down') return <ArrowRight size={16} className="rotate-90" />;
            return <Users size={16} />;
        }
        if (type === 'leader') {
            if (mode === 'recursive') return <Network size={16} />;
            return <ArrowRight size={16} className="-rotate-45" />;
        }
        if (type === 'schedulegroup') return <DoorOpen size={16} />;
        return <Users size={16} />;
    };

    const getTypeLabel = (type: string, mode?: string) => {
        if (type === 'person') return 'Person';
        if (type === 'role') return 'Role';
        if (type === 'group') {
            if (mode === 'up') return 'Hierarchy Up';
            if (mode === 'down') return 'Hierarchy Down';
            return 'Group';
        }
        if (type === 'leader') {
            if (mode === 'recursive') return 'Leaders (Recursive)';
            return 'Direct Leader';
        }
        if (type === 'schedulegroup') return 'Schedule Groups';
        return type;
    };

    const renderTiles = () => (
        <div className="grid grid-cols-2 gap-3">
            {[
                { id: 'person', label: 'Person', icon: UserCheck, desc: 'Specific user' },
                { id: 'role', label: 'Role', icon: Briefcase, desc: 'Users with role' },
                { id: 'group', label: 'Group', icon: Users, desc: 'Department/Team' },
                { id: 'leader', label: 'Leader', icon: Network, desc: 'Managerial line' },
                { id: 'schedulegroup', label: 'Schedule', icon: DoorOpen, desc: 'Time-based group' },
            ].map(tile => (
                <button
                    key={tile.id}
                    onClick={() => handleTypeSelect(tile.id as AssignmentType)}
                    className="flex flex-col items-start p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 hover:border-teal-500 hover:ring-1 hover:ring-teal-500/20 transition-all group text-left"
                >
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-500 group-hover:text-teal-600 group-hover:bg-teal-50 dark:group-hover:bg-teal-900/30 flex items-center justify-center mb-3 transition-colors">
                        <tile.icon size={18} />
                    </div>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                        {tile.label}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                        {tile.desc}
                    </span>
                </button>
            ))}
        </div>
    );

    const renderForm = () => {
        // @ts-ignore
        const TypeIcon = getTypeIcon(config.type, config.mode as string)?.type || Users;

        return (
            <div className="space-y-5 animate-in slide-in-from-right-2 duration-200">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
                    <div className="p-1.5 bg-slate-100 dark:bg-slate-800 rounded-md">
                        {/* @ts-ignore */}
                        <TypeIcon size={14} className="text-slate-600 dark:text-slate-400" />
                    </div>
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">Configure Assignment</h4>
                </div>

                {/* --- Person Form --- */}
                {config.type === 'person' && (
                    <AsyncSelect 
                        label="Select Person" 
                        fetchOptions={(params) => services.directory.getPersons(params)}
                        fetchItem={(id) => services.directory.getItemById('person', id)}
                        value={config.param}
                        onChange={(id, item) => setConfig({ ...config, param: id, paramName: item?.name })}
                    />
                )}

                {/* --- Role Form --- */}
                {config.type === 'role' && (
                    <AsyncSelect 
                        label="Select Role" 
                        fetchOptions={(params) => services.directory.getRoles(params)}
                        fetchItem={(id) => services.directory.getItemById('role', id)}
                        value={config.param}
                        onChange={(id, item) => setConfig({ ...config, param: id, paramName: item?.name })}
                    />
                )}

                {/* --- Group Form --- */}
                {config.type === 'group' && (
                    <div className="space-y-4">
                        <AsyncSelect 
                            label="Select Group" 
                            fetchOptions={(params) => services.directory.getGroups(params)}
                            fetchItem={(id) => services.directory.getItemById('group', id)}
                            value={config.param}
                            onChange={(id, item) => setConfig({ ...config, param: id, paramName: item?.name })}
                        />
                        
                        <div className="space-y-2">
                             <label className="block text-[10px] font-bold text-slate-500 uppercase">Direction & Scope</label>
                             <div className="flex gap-2">
                                {['exact', 'up', 'down'].map(m => (
                                    <button
                                        key={m}
                                        onClick={() => setConfig({...config, mode: m as any})}
                                        className={`flex-1 py-2 px-2 text-xs font-medium rounded-lg border transition-all text-center ${config.mode === m ? 'bg-teal-600 border-teal-600 text-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-600'}`}
                                    >
                                        {m === 'exact' ? 'This Group' : m === 'up' ? 'Upwards' : 'Downwards'}
                                    </button>
                                ))}
                             </div>
                        </div>

                        {config.mode === 'exact' && (
                            <AsyncSelect 
                                label="Filter by Role (Optional)" 
                                placeholder="Any Role"
                                fetchOptions={(params) => services.directory.getRoles(params)}
                                fetchItem={(id) => services.directory.getItemById('role', id)}
                                value={config.arg}
                                onChange={(id, item) => setConfig({ ...config, arg: id, argName: item?.name })}
                            />
                        )}
                    </div>
                )}

                {/* --- Leader Form --- */}
                {config.type === 'leader' && (
                    <div className="space-y-4">
                        <AsyncSelect 
                            label="Leader of Person" 
                            fetchOptions={(params) => services.directory.getPersons(params)}
                            fetchItem={(id) => services.directory.getItemById('person', id)}
                            value={config.param}
                            onChange={(id, item) => setConfig({ ...config, param: id, paramName: item?.name })}
                        />
                        
                        <label className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors">
                            <input 
                                type="checkbox"
                                checked={config.mode === 'recursive'}
                                onChange={(e) => setConfig({...config, mode: e.target.checked ? 'recursive' : 'direct'})}
                                className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 w-4 h-4"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">Recursive (All managers upwards)</span>
                        </label>
                    </div>
                )}

                {/* --- Common: Blacklist --- */}
                <div className="pt-2">
                    <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${config.blacklist ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-900' : 'bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-800'}`}>
                        <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-colors shrink-0 ${config.blacklist ? 'bg-red-500 border-red-500' : 'bg-white border-slate-300'}`}>
                             {config.blacklist && <Check size={10} className="text-white" />}
                        </div>
                        <input 
                            type="checkbox"
                            checked={config.blacklist}
                            onChange={(e) => setConfig({...config, blacklist: e.target.checked})}
                            className="hidden"
                        />
                        <div className="flex-1">
                            <span className={`block text-sm font-bold ${config.blacklist ? 'text-red-700 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>Blacklist Assignment</span>
                            <span className={`block text-[10px] mt-0.5 ${config.blacklist ? 'text-red-600/70' : 'text-slate-500'}`}>Exclude users matching this rule</span>
                        </div>
                    </label>
                </div>

                <div className="flex gap-2 pt-2">
                    <button 
                        onClick={() => { setIsAdding(false); setSelectedType(null); }}
                        className="flex-1 py-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-xs font-bold transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleAdd}
                        disabled={config.type !== 'schedulegroup' && !config.param}
                        className="flex-[2] py-2.5 bg-slate-900 dark:bg-teal-600 hover:bg-black dark:hover:bg-teal-700 text-white rounded-lg text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                        Add Rule
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 space-y-6">
            <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-100 dark:border-slate-800 flex gap-3">
                <Users className="text-slate-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Assignment Rules</p>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                        Define who can claim or be assigned to this task. Rules are evaluated sequentially.
                    </p>
                </div>
            </div>

            {/* List of existing assignments */}
            {!isAdding && (
                <div className="space-y-3">
                    {assignments.length === 0 && (
                        <div className="text-center py-10 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/50">
                            <div className="w-10 h-10 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-lg flex items-center justify-center mx-auto mb-3 text-slate-300">
                                <UserPlus size={18}/>
                            </div>
                            <p className="text-xs text-slate-500 font-medium">No active assignments</p>
                        </div>
                    )}

                    <div className="space-y-1">
                        {assignments.map((str, idx) => {
                            const parsed = parseAssignment(str);
                            return (
                                <div key={idx} className="group flex items-center gap-3 p-3 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm hover:border-slate-300 transition-colors">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${parsed.blacklist ? 'bg-red-50 text-red-500' : 'bg-slate-50 text-slate-500'}`}>
                                        {parsed.blacklist ? <ShieldAlert size={14} /> : getTypeIcon(parsed.type as string, parsed.mode)}
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs font-bold ${parsed.blacklist ? 'text-red-700' : 'text-slate-700 dark:text-slate-200'}`}>
                                                {getTypeLabel(parsed.type as string, parsed.mode)}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-slate-500 font-mono truncate flex items-center gap-1">
                                            {parsed.param}
                                            {parsed.arg && <span className="text-teal-600">@{parsed.arg}</span>}
                                        </div>
                                    </div>

                                    <button 
                                        onClick={() => handleRemove(idx)}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <button 
                        onClick={() => setIsAdding(true)}
                        className="w-full py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-600 dark:text-slate-300 hover:border-teal-500 hover:text-teal-600 dark:hover:text-teal-400 transition-all flex items-center justify-center gap-2 text-xs font-bold shadow-sm"
                    >
                        <Plus size={14} /> Add Assignment
                    </button>
                </div>
            )}

            {/* Add New Section */}
            {isAdding && (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl rounded-xl overflow-hidden animate-in zoom-in-95 duration-200">
                    <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex justify-between items-center">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                            {isManualEntry ? 'Manual Entry' : selectedType ? 'New Rule' : 'Select Type'}
                        </h4>
                        <div className="flex items-center gap-2">
                            {!selectedType && (
                                <button
                                    onClick={() => setIsManualEntry(!isManualEntry)}
                                    className={`text-xs font-medium flex items-center gap-1 px-2 py-1 rounded transition-colors ${isManualEntry ? 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    <Keyboard size={12} /> Manual
                                </button>
                            )}
                            {!selectedType && (
                                <button onClick={() => { setIsAdding(false); setIsManualEntry(false); }} className="text-xs text-slate-400 hover:text-slate-600 font-medium">Close</button>
                            )}
                        </div>
                    </div>

                    <div className="p-4">
                        {isManualEntry ? (
                            <div className="space-y-3 animate-in fade-in duration-200">
                                <p className="text-[11px] text-slate-500 leading-relaxed">
                                    Type a raw assignment string. Format: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-[10px]">type:value</code> (e.g. <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-[10px]">role:BadgeOffice</code>, <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-[10px]">person:123</code>, <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-[10px]">!group:Security</code>)
                                </p>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={manualValue}
                                        onChange={(e) => setManualValue(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleManualAdd()}
                                        placeholder="e.g. role:BadgeOffice"
                                        className="flex-1 px-3 py-2 text-sm font-mono border rounded-lg border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20"
                                    />
                                    <button
                                        onClick={handleManualAdd}
                                        disabled={!manualValue.trim()}
                                        className="px-4 py-2 bg-slate-900 dark:bg-teal-600 hover:bg-black dark:hover:bg-teal-700 text-white rounded-lg text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        ) : selectedType ? renderForm() : renderTiles()}
                    </div>
                </div>
            )}
        </div>
    );
};
